import React, { useMemo } from 'react';
import { useReactiveVar } from '@apollo/client';
import PropTypes from 'prop-types';
import _ from 'lodash';

import PublishedProposalModal from './PublishedProposalModal/PublishedProposalModal';
import UnPublishedProposalModal from './UnPublishedProposalModal/UnPublishedProposalModal';
import ClientPaymentModal from './ClientPaymentModal/ClientPaymentModal';
import PaymentStatusModal from './PaymentStatusModal/PaymentStatusModal';
import DownloadingPDFModal from '../../../Modals/DownloadingPDFModal';
import DownloadPdfErrorModal from '../../../Modals/DownloadPdfErrorModal';
import { IsEditingModal } from 'pages/Proposal/components/Draft/DraftModals';
import { scheduleSuccessModalVar } from 'graphql/cache';
import { getEffectiveCheckboxOption } from 'helpers/proposal';
import { ScheduleSuccessModel } from './PublishedProposalModal/ScheduleSuccessModel';

const PublishModals = ({
  proposalPublished,
  proposalUnPublished,
  makePayment,
  paymentStatus,
  downloadPDFError,
  setProposalUnPublished,
  setProposalPublished,
  setProposal,
  setMakePayment,
  setPaymentStatus,
  setDownloadPDFError,
  configText,
  copyPropsalLink,
  publishedProposalLink,
  downloadPdf,
  clientMakesPayment,
  proposal,
  paymentError,
  setPaymentError,
  makingPayment,
  history,
  isEditingModal,
  isHistory,
}) => {
  const scheduleSuccessModal = useReactiveVar(scheduleSuccessModalVar);

  // Calculate payment amount based on pricing strategy and payment timing
  const calculatePaymentAmount = useMemo(() => {
    const enablePayment = proposal?.enablePayment;
    let selectedOption = proposal?.selectedOption;
    const hasSigned = proposal?.signature?.date;

    // Helper function to calculate discount/tax adjusted price
    const calcDiscPrice = (price, discount, symbol, isDotSeparator) => {
      if (isDotSeparator) {
        discount = Number(String(discount).replaceAll(',', '.'));
        price = Number(String(price).replaceAll(',', '.'));
      }
      if (discount && price) {
        if (symbol === '%') {
          if (discount >= 100) {
            if (discount > 100) return price;
          }
          price = price - price * (discount / 100);
        } else {
          price = price - discount;
        }
      }
      return price;
    };

    // Helper function to calculate subtotal for a chosen option (per-item checkbox support)
    const calculateSubTotal = (chosen) => {
      if (!chosen?.list) return 0;

      let totes = 0;
      const globalSelectable = chosen.selectable;

      chosen.list.forEach((item) => {
        const discVal = item?.discount?.value || 0;
        const discUnit = item?.discount?.unit || '%';

        const price =
          proposal.priceSeperator === '.'
            ? item?.currentPrice
              ? String(item?.currentPrice)?.replaceAll(',', '.')
              : String(item?.price)?.replaceAll(',', '.')
            : item?.currentPrice
            ? item?.currentPrice
            : item?.price;

        const disc =
          proposal.priceSeperator === '.' ? String(discVal)?.replaceAll(',', '.') : discVal;

        const checkboxOpt = getEffectiveCheckboxOption(item, globalSelectable);
        const itemCounts =
          !checkboxOpt.on ||
          (typeof item.checked !== 'undefined' ? item.checked : checkboxOpt.markedByDefault);

        let subTotal = 0;

        if (itemCounts) {
          subTotal = calcDiscPrice(
            parseFloat(price * (chosen?.quantity ? item.items || 1 : 1)),
            parseFloat(disc * (discUnit !== '%' && chosen?.quantity ? item.items || 1 : 1)),
            discUnit,
            proposal.priceSeperator === '.'
          );
        }
        totes += Number(subTotal) || 0;
      });

      const hasNoCheckboxes = !chosen?.list?.some(
        (item) => getEffectiveCheckboxOption(item, globalSelectable).on
      );

      if (hasNoCheckboxes && proposal.clientPayableAmount > totes) {
        return proposal.clientPayableAmount;
      }

      return totes;
    };

    // Helper function to calculate total with discount and tax
    const calculateTotal = (subTotal, chosen) => {
      let total = subTotal;

      // Apply discount
      if (chosen?.discount?.val) {
        const discVal = chosen.discount.val;
        const discUnit = chosen.discount.unit || '%';
        total = calcDiscPrice(total, discVal, discUnit, proposal.priceSeperator === '.');
      }

      // Apply tax
      if (chosen?.tax?.val) {
        const taxVal = chosen.tax.val;
        const taxUnit = chosen.tax.unit || '%';
        const taxAmount = taxUnit === '%' ? (total * taxVal) / 100 : taxVal;
        total = total + taxAmount;
      }

      return total;
    };

    // Find the pricing table in the draft
    const findTableInProp = () => {
      let tables = [];
      _.each(proposal.draft, (section, sectionKey) => {
        if (
          section &&
          section.raw &&
          section.raw.entityMap &&
          proposal.draft.sectionorder.indexOf(sectionKey) > -1
        ) {
          _.each(section.raw.entityMap, (v, k) => {
            if (v.data && v.data.texcontent) {
              try {
                const table = JSON.parse(v.data.texcontent);
                if (table && table.deliverables) {
                  tables.push(table);
                }
              } catch (exx) {}
            }
          });
        }
      });
      return tables.length === 1 && tables[0];
    };

    // Default to integration amount
    let defaultAmount = proposal?.clientPayableAmount || 0;
    let amount = defaultAmount;

    try {
      const table = findTableInProp();

      if (!table?.deliverables?.chosen) {
        return amount; // Use integration amount if no table found
      }

      // Get the pricing strategy from deliverables.chosen (it's copied to each option)
      // Fallback to root level pricing if not available
      const [firstChosenKey] = Object.keys(table.deliverables.chosen);
      const firstChosen = table.deliverables.chosen[firstChosenKey];
      const pricingStrategy = firstChosen?.pricing?.strategy || table?.pricing?.strategy || 'table';

      // TABLE PRICING
      if (pricingStrategy === 'table') {
        // After sign: use selected option amount
        if (enablePayment === 'after_sign' && hasSigned) {
          const chosen = table.deliverables.chosen['OPTION 1'];
          if (chosen) {
            const subTotal = calculateSubTotal(chosen);
            amount = calculateTotal(subTotal, chosen);
          }
        } else if (enablePayment === 'before_sign') {
          const chosen = table.deliverables.chosen['OPTION 1'];

          if (chosen) {
            // If no items have checkbox, treat as selected. Else require at least one checkbox item checked/markedByDefault.
            const anyWithCheckbox = chosen.list?.some(
              (item) => getEffectiveCheckboxOption(item, chosen.selectable).on
            );
            const hasCheckedItems = !anyWithCheckbox
              ? true
              : chosen.list?.some((item) => {
                  const opt = getEffectiveCheckboxOption(item, chosen.selectable);
                  if (!opt.on) return false;
                  return typeof item.checked !== 'undefined' ? item.checked : opt.markedByDefault;
                });

            if (hasCheckedItems) {
              // Calculate amount based on selected checkboxes
              const subTotal = calculateSubTotal(chosen);
              amount = calculateTotal(subTotal, chosen);
            }
            // If no checkbox is selected, amount stays as integration amount (default)
          }
        }
      }
      // VALUE PRICING
      else if (pricingStrategy === 'value') {
        // Check if there are multiple options for value pricing
        const hasMultipleOptions = Object.keys(table.deliverables.chosen).length > 1;

        if (hasMultipleOptions && selectedOption) {
          // Find object with name selectedOption
          const chosen =
            Object.values(table.deliverables.chosen).find(
              (option) => option.name?.toUpperCase() === selectedOption.toUpperCase()
            ) || table.deliverables.chosen[selectedOption.toUpperCase()];

          if (chosen) {
            let subTotal;
            if (chosen.subtotalValue !== undefined && chosen.subtotalValue !== null) {
              subTotal = chosen.subtotalValue;
            } else {
              subTotal = calculateSubTotal(chosen);
            }
            amount = calculateTotal(subTotal, chosen);
          }
        } else if (!hasMultipleOptions) {
          // If only one option, use it regardless of selection
          const [firstKey] = Object.keys(table.deliverables.chosen);
          const chosen = table.deliverables.chosen[firstKey];

          if (chosen) {
            let subTotal;
            if (chosen.subtotalValue !== undefined && chosen.subtotalValue !== null) {
              subTotal = chosen.subtotalValue;
            } else {
              subTotal = calculateSubTotal(chosen);
            }
            amount = calculateTotal(subTotal, chosen);
          }
        }
        // If multiple options but none selected, fall back to integration amount
      }
    } catch (error) {
      console.error('Error calculating payment amount:', error);
      // Fallback to integration amount
      amount = defaultAmount;
    }

    if (amount <= 0) {
      amount = defaultAmount;
    }

    return amount;
  }, [proposal]);

  return (
    <>
      {proposalPublished && (
        <PublishedProposalModal
          copyPropsalLink={copyPropsalLink}
          publishedProposalLink={publishedProposalLink}
          onCancel={() => setProposalPublished(false)}
          configText={configText}
          downloadPdf={downloadPdf}
          proposal={proposal}
          setInternalProp={setProposal}
        />
      )}
      {proposalUnPublished && (
        <UnPublishedProposalModal
          onCancel={() => setProposalUnPublished(false)}
          configText={configText}
        />
      )}
      {scheduleSuccessModal && (
        <ScheduleSuccessModel
          isOpen={true}
          onCancel={() => scheduleSuccessModalVar(false)}
          scheduleInfo={scheduleSuccessModal}
        />
      )}
      {makePayment && (
        <ClientPaymentModal
          makingPayment={makingPayment}
          clientPayableLabel={proposal.clientPayableLabel || 'Make Payment'}
          amount={`${calculatePaymentAmount} ${
            proposal.clientPayableCurrency
              ? proposal.clientPayableCurrency.toUpperCase()
              : proposal.stripe.currency
              ? proposal.stripe.currency.toUpperCase()
              : 'USD'
          }`}
          onConfirm={() => clientMakesPayment(calculatePaymentAmount?.toString())}
          onCancel={() => {
            setMakePayment(false);
            setPaymentError('');
          }}
        />
      )}
      {paymentStatus && (
        <PaymentStatusModal
          paymentStatus={paymentStatus}
          onCancel={() => {
            setPaymentStatus('');
            history.push(`/cd/${proposal._id}`);
          }}
        />
      )}
      <DownloadingPDFModal />
      {downloadPDFError && (
        <DownloadPdfErrorModal
          downloadPDFError={downloadPDFError}
          onCancel={() => setDownloadPDFError('')}
        />
      )}

      {!isHistory &&
        isEditingModal &&
        proposal.state !== 'approved' &&
        proposal.state !== 'declined' && (
          <IsEditingModal isEditingModal={isEditingModal} isClient={true} />
        )}
    </>
  );
};

PublishModals.defaultProps = {
  proposal: '',
  makingPayment: false,
  paymentError: '',
  proposalPublished: false,
  proposalUnPublished: false,
  makePayment: false,
  paymentStatus: false,
  downloadingPDF: false,
  downloadPDFError: '',
  setProposalUnPublished: () => {},
  setProposalPublished: () => {},
  setMakePayment: () => {},
  setPaymentStatus: () => {},
  setDownloadPDFError: () => {},
  configText: () => {},
  publishedProposalLink: () => {},
  copyPropsalLink: () => {},
  downloadPdf: () => {},
  clientMakesPayment: () => {},
};

PublishModals.propTypes = {
  history: PropTypes.instanceOf(Object).isRequired,
  proposal: PropTypes.oneOfType([PropTypes.instanceOf(Object), PropTypes.string]),
  paymentError: PropTypes.string,
  makingPayment: PropTypes.bool,
  proposalPublished: PropTypes.bool,
  proposalUnPublished: PropTypes.bool,
  makePayment: PropTypes.bool,
  downloadingPDF: PropTypes.bool,
  paymentStatus: PropTypes.string,
  downloadPDFError: PropTypes.string,
  setProposalUnPublished: PropTypes.func,
  setProposalPublished: PropTypes.func,
  setMakePayment: PropTypes.func,
  setPaymentStatus: PropTypes.func,
  setDownloadPDFError: PropTypes.func,
  publishedProposalLink: PropTypes.func,
  configText: PropTypes.func,
  copyPropsalLink: PropTypes.func,
  downloadPdf: PropTypes.func,
  clientMakesPayment: PropTypes.func,
};

export default PublishModals;
